const CHANNEL_NAME = 'ruut-voice-call-ownership';
const LEASE_PREFIX = 'ruut:voice-call-owner:';
const LEASE_TTL_MS = 120000;
const LEASE_REFRESH_MS = 5000;

const parseLease = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export class CallTabCoordinator extends EventTarget {
  constructor({
    storage = window.localStorage,
    Channel = window.BroadcastChannel,
    Locks = navigator.locks,
  } = {}) {
    super();
    this.storage = storage;
    this.tabId = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    this.ownedCallSids = new Set();
    this.lockReleases = new Map();
    this.locks = Locks;
    this.channel = Channel ? new Channel(CHANNEL_NAME) : null;
    this.channel?.addEventListener('message', this.handleMessage);
    this.refreshTimer = window.setInterval(
      this.refreshLeases,
      LEASE_REFRESH_MS
    );
  }

  owner(callSid) {
    const lease = parseLease(
      this.storage.getItem(CallTabCoordinator.leaseKey(callSid))
    );
    if (!lease || lease.expiresAt <= Date.now()) {
      this.storage.removeItem(CallTabCoordinator.leaseKey(callSid));
      return null;
    }
    return lease.tabId;
  }

  isOwnedElsewhere(callSid) {
    const owner = this.owner(callSid);
    return !!owner && owner !== this.tabId;
  }

  async claim(callSid) {
    if (!callSid || this.isOwnedElsewhere(callSid)) return false;

    if (this.locks && !this.ownedCallSids.has(callSid)) {
      const acquired = await this.acquireLock(callSid);
      if (!acquired) return false;
    }

    this.ownedCallSids.add(callSid);
    this.writeLease(callSid);
    this.channel?.postMessage({
      type: 'call-claimed',
      callSid,
      tabId: this.tabId,
    });
    return true;
  }

  release(callSid) {
    if (!callSid) return;
    this.ownedCallSids.delete(callSid);
    this.lockReleases.get(callSid)?.();
    this.lockReleases.delete(callSid);
    if (this.owner(callSid) === this.tabId) {
      this.storage.removeItem(CallTabCoordinator.leaseKey(callSid));
    }
  }

  close() {
    this.releaseAll();
    window.clearInterval(this.refreshTimer);
    this.channel?.removeEventListener('message', this.handleMessage);
    this.channel?.close();
  }

  releaseAll() {
    [...this.ownedCallSids].forEach(callSid => this.release(callSid));
  }

  handleMessage = event => {
    const { type, callSid, tabId } = event.data || {};
    if (type !== 'call-claimed' || !callSid || tabId === this.tabId) return;
    this.dispatchEvent(
      new CustomEvent('call:claimed-elsewhere', { detail: { callSid } })
    );
  };

  refreshLeases = () => {
    this.ownedCallSids.forEach(callSid => this.writeLease(callSid));
  };

  writeLease(callSid) {
    this.storage.setItem(
      CallTabCoordinator.leaseKey(callSid),
      JSON.stringify({
        tabId: this.tabId,
        expiresAt: Date.now() + LEASE_TTL_MS,
      })
    );
  }

  acquireLock(callSid) {
    return new Promise(resolve => {
      this.locks
        .request(
          `ruut-voice-call:${callSid}`,
          { ifAvailable: true, mode: 'exclusive' },
          lock => {
            if (!lock) {
              resolve(false);
              return undefined;
            }

            return new Promise(release => {
              this.lockReleases.set(callSid, release);
              resolve(true);
            });
          }
        )
        .catch(() => resolve(false));
    });
  }

  static leaseKey(callSid) {
    return `${LEASE_PREFIX}${callSid}`;
  }
}

let coordinator;

export const getCallTabCoordinator = () => {
  coordinator ||= new CallTabCoordinator();
  return coordinator;
};
